// Copyright: IQGeo Limited 2010-2019
import $ from 'jquery';
import _ from 'underscore';
import myw from 'myWorld-base';
import 'myWorld/controls/geomDrawMode';
import './stringFieldEditor';
import './enumeratorFieldEditor';
import './booleanFieldEditor';
import './dateFieldEditor';
import './referenceFieldEditor';
import './timeFieldEditor';
import './integerFieldEditor';
import './doubleFieldEditor';
import './numericFieldEditor';
import './linkFieldEditor';
import './imageFieldEditor';
import './barcodeFieldEditor';
import './fileFieldEditor';
import './sharingFieldEditor';

/**
 * Options for FieldEditorsPanel instances
 * @typedef fieldEditorsPanelOptions
 * @property {DDFeature}            feature                     The feature being edited
 * @property {Array<string>}            fields                      Internal names of fields to include, in display order
 * @property {domElement}               el                          (from Backbone) Element where the editor should render the UI. If not provided, a popup editor will be used
 * @property {boolean}          [useExpandedFieldEditors=false]     Whether the FieldEditor should use more space if useful (ex: button instead of dropdown) or if it should remain compact
 * @property {object<FieldEditor|function>}  [fieldEditorMapping]  Field type to FieldEditor mapping. Keyed on field type. <br/>
 *                                                                   If the value is a function instead of a class, it gets fieldDD as argument and it should return the appropriate class.
 */

myw.FieldEditorsPanel = myw.Control.extend({
    options: {
        useExpandedFieldEditors: false,
        fieldEditorMapping: {}
    },

    defaultMapping: {
        string: fieldDD =>
            fieldDD.hasOwnProperty('enum') ? myw.EnumeratorFieldEditor : myw.StringFieldEditor,
        boolean: myw.BooleanFieldEditor,
        date: myw.DateFieldEditor,
        reference: myw.ReferenceFieldEditor,
        reference_set: myw.ReferenceFieldEditor,
        foreign_key: myw.ReferenceFieldEditor,
        timestamp: myw.TimeFieldEditor,
        integer: myw.IntegerFieldEditor,
        double: myw.DoubleFieldEditor,
        numeric: myw.NumericFieldEditor,
        link: myw.LinkFieldEditor,
        image: myw.ImageFieldEditor,
        file: myw.FileFieldEditor
    },

    /**
     * @class A UI Control to a hold a list of field editors. <br/>
     *        Is responsible for the layout and label of each field editor. <br/>
     *        The rendering of each field editor is delegated to an appropriate subclass of {@link FieldEditor} <br/>
     *        The choice of which FieldEditor to use for a particular field is made based on the field's type. <br/>
     *        This mapping can be overriden by providing a mapping in fieldEditorMapping option (in a subclass or passing to the constructor)
     * @example new myw.FieldEditorsPanel(owner, {feature: aFeature, fields: someFieldNames, el: aContainer}) <br/>
     * @example var panel = new myw.FieldEditorsPanel(owner);
     * panel.buildForm(aFeature, someFieldNames, aContainer);
     * @param  {Application|Control}    owner
     * @param  {fieldEditorsPanelOptions}       [options]
     * @constructs
     * @extends {Control}
     */
    initialize(owner, options) {
        myw.Control.prototype.initialize.call(this, owner, options);

        //merge fieldEditorMapping provided options with default mapping
        this.options.fieldEditorMapping = _.extend(
            {},
            this.defaultMapping,
            this.options.fieldEditorMapping
        );

        if (this.options.feature) {
            this.buildForm(options.feature, options.fields, this.$el);
        }
    },

    /**
     * Creates a form for editing a feature's properties
     * @param  {DDFeature}  feature     Feature to edit
     * @param  {Array<string>}  fields      Internal names of fields to include, in display order
     * @param  {jQueryElement}  container   jquery object for an element on which to build the form
     */
    buildForm(feature, fields, container) {
        //get all fieldDDs in display order
        const fieldDDs = _.map(fields, fieldName => feature.featureDD.fields[fieldName]),
            tableEl = $('<table>', { class: 'field-editor-table' });

        this.fieldEditors = {};

        container.append(tableEl);

        //build the form for editing
        _.each(fieldDDs, fieldDD => {
            const fieldName = fieldDD.internal_name,
                readonlyFields = _.result(feature, 'readonlyFields');
            if (
                _.contains(readonlyFields, fieldDD.internal_name) || // field is in the edit exclude list
                _.contains(['point', 'polygon', 'linestring'], fieldDD.type) || // geometry field
                fieldDD.hasOwnProperty('generator') || //generated field
                !!fieldDD.value
            ) {
                //calculated field
                return; //do nothing
            } else {
                // build the form element for this field
                this.buildFormElements(feature, fieldName, fieldDD, tableEl);
            }
        });

        _.each(this.fieldEditors, fieldEditor => {
            this.listenTo(fieldEditor, 'change', this._propagateEvent);
        });
    },

    _propagateEvent(ev) {
        this.trigger('change', ev);
    },

    /**
     * Builds the form elements for a given field
     * @param  {Feature}    feature     Feature being edited
     * @param  {string}         fieldName   Internal name of the field
     * @param  {fieldDD}        fieldDD
     * @param  {jQueryElement}  container   jQuery object for an element on which to add the form elements for the specified field
     */
    buildFormElements(feature, fieldName, fieldDD, container) {
        let fieldEditor, inputElement, rowElement, fieldNameElement, valueElement; // includes input, unit and validation elements

        fieldEditor = this.getFieldEditor(feature, fieldDD);
        this.fieldEditors[fieldName] = fieldEditor;
        inputElement = fieldEditor.$el;

        // html <tr> element
        rowElement = $('<tr></tr>').addClass('atts');

        fieldNameElement = $(`<th class="field-name-display">${fieldDD.external_name}</th>`);

        // add an asterick to required fields.
        const isKeyField = fieldDD.internal_name === feature.keyFieldName;
        if (fieldDD.mandatory || isKeyField) fieldNameElement.append(this._addAsterisk());

        rowElement.append(fieldNameElement);

        valueElement = $('<td></td>').attr('class', 'feature-edit-input');
        inputElement.attr('name', fieldDD.external_name);
        valueElement.append(inputElement);

        valueElement.append("<div class='inlineValidation'></div>");

        rowElement.append(valueElement);

        container.append(rowElement);
    },

    /**
     * Returns a field editor instance, apropriate to edit a given field
     * @param  {DDFeature}  feature     Feature being edited. Will be used to check if a custom field editor should be used
     * @param  {fieldDD}        fieldDD     Data dictionary information of the field to edit
     * @return {FieldEditor}
     */
    getFieldEditor(feature, fieldDD) {
        /* jshint newcap: false */
        let editorClass;

        let fieldType = fieldDD.type;
        const bracketIndex = fieldType.indexOf('(');
        let customFieldEditor;

        const editorOptions = {
            expandedMode: this.options.useExpandedFieldEditors
        };

        //strip parentesis section from field type
        if (bracketIndex > 0) {
            fieldType = fieldType.slice(0, bracketIndex);
        }

        //check if there is a specific field editor defined for this feature&field
        customFieldEditor = feature.getCustomFieldEditorFor(fieldDD);
        if (customFieldEditor) {
            editorClass = customFieldEditor;
        } else {
            const classOrFunction = this.options.fieldEditorMapping[fieldType];

            //a class is also a function (a constructor)
            //figure out which by checking if it has 'extend' is defined (as it has for a class)
            if (typeof classOrFunction == 'function' && classOrFunction.hasOwnProperty('extend')) {
                //class
                editorClass = classOrFunction;
            } else if (typeof classOrFunction == 'function') {
                editorClass = classOrFunction(fieldDD);
            }

            if (!editorClass) {
                throw new Error(
                    `No field editor class for field ${fieldDD.internal_name}. Type: ${fieldDD.type}`
                );
            }
        }
        return new editorClass(this, feature, fieldDD, editorOptions);
    },

    /**
     * Adds an asterisk next to the field editor of fields which are mandatory
     * @return {html}
     * @private
     */
    _addAsterisk() {
        return $('<span>*</span>').addClass('required-field-notification');
    },

    /* **************************  Field value manipulation  ********************* */

    /**
     * Obtain values, including the changes made by the user, to send to the database
     * Values are obtained from the field editors (html input elements)
     * @param  {DDFeature}  feature     Feature being inserted/updated
     * @return {object}         Values to save, keyed on field name
     */
    getValues(feature) {
        const properties = {};

        for (const fieldName in this.fieldEditors) {
            const fieldEditor = this.fieldEditors[fieldName],
                value = fieldEditor.getValue();
            if (value !== undefined) {
                properties[fieldName] = value;
            }
        }

        return properties;
    },

    /* ************************ Form Validation ********************* */

    /**
     * Validates the geometry and field values according to the data dictionary
     * If a value is invalid this information is presented to the user
     * @param  {geojson}    featureJson Feature data to be validated
     * @return {boolean}                Whether the provided data is valid or not
     */
    validateChanges(featureJson) {
        let fieldError = false,
            fieldEditor,
            value,
            validationResult;
        this.removeValidationHighlight();

        for (const fieldName in this.fieldEditors) {
            if (featureJson.properties.hasOwnProperty(fieldName)) {
                fieldEditor = this.fieldEditors[fieldName];
                value = featureJson.properties[fieldName];
                validationResult = fieldEditor.validate(value);

                if (validationResult !== true) {
                    if (typeof fieldEditor.control !== 'undefined' && null !== fieldEditor.control)
                        fieldEditor.control.$el.addClass('validationHighlight');
                    // for 'number' fields with units
                    else fieldEditor.$el.addClass('validationHighlight');
                    // Add inline validation message
                    fieldEditor.$el.siblings('.inlineValidation').html(validationResult);
                    fieldError = true;
                }
            }
        }

        return !fieldError;
    },

    /**
     * Remove validation errors if any
     */
    removeValidationHighlight() {
        this.$el.find('.validationHighlight').removeClass('validationHighlight');
        this.$el.find('.inlineValidation').html('');
    },

    /**
     * Removes it self from the dom
     */
    close() {
        this.remove();
        _.each(this.fieldEditors, fieldEditor => {
            fieldEditor.remove();
        });
    }
});

export default myw.FieldEditorsPanel;
